/**
 * Parent edit form.
 * Loads full state, hydrates the form, then PATCHes personal-account + contract in one submit.
 */
import * as React from 'react';
import { useGetParentRemoteFullState, useUpdateParentRemoteProfile } from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { parentEditValidationSchema } from '@/utils/validation-schemas-utils';
import {
  applyContactSwapUnified,
  areContactsIdentical,
  isPrimaryContactDirty,
  isPrimaryContactFormValid,
  isPrimaryContactSubmitBlocked,
  readPhoneSetupLink,
  toPrimaryContactMode,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { ContactChangeModal } from '@/components/common/modals/ContactChangeModal';
import { useContactChangeOnSubmit } from '@/hooks/useContactChangeOnSubmit';
import { resolveContactCheckType, useContactExistsCheck } from '@/hooks/useContactExistsCheck';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import type { ParentFormValues } from '@/types/parent.types';
import {
  buildUpdateParentDto,
  defaultParentUpload,
  mergePendingUploads,
  hydrateParentFormState,
  parentValidationPayload,
  resolveKnownContactType,
  useParentFormHandlers,
  type ParentContactType,
} from '@/components/parents/parent-form-utils';
import { ParentPersonalAccountSection } from '@/components/parents/parent-form-builder/ParentPersonalAccountSection';
import { ParentContractSection } from '@/components/parents/parent-form-builder/ParentContractSection';
import { buildPendingDocumentFilePatch } from '@/utils/pending-document-link-utils';
import { hasPendingUploadFiles, uploadPendingFiles } from '@/utils/pending-upload-utils';

/** Props for {@link EditParentView}. */
interface EditParentViewProps {
  userId: string;
  stepKey?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Parent edit form. */
export function EditParentView({
  userId,
  stepKey: _stepKey,
  onClose,
  onSuccess,
}: Readonly<EditParentViewProps>) {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const defaultCountry = useDefaultPhoneCountry();
  const validation = useFormValidation(parentEditValidationSchema);

  const {
    data: fullState,
    isLoading: isLoadingFullState,
    isError: isFullStateError,
    error: fullStateError,
  } = useGetParentRemoteFullState(userId, companyId, { enabled: !!userId && !!companyId });
  useUnitGetNotFound({
    isError: isFullStateError,
    error: fullStateError,
    catalogKey: 'parentNotFound',
    onClose,
    entityId: userId,
  });

  const record = fullState?.record;

  const [formData, setFormData] = React.useState<ParentFormValues>({});
  const [contactType, setContactType] = React.useState<ParentContactType>('unknown');
  const [pendingFiles, setPendingFiles] = React.useState<Record<string, File | null>>({});
  const [isUploading, setIsUploading] = React.useState(false);

  const primaryContactValue = formData.contact || '';
  const secondaryContactValue = formData.secondaryContact || '';
  const secondaryContactType: 'email' | 'phone' = contactType === 'email' ? 'phone' : 'email';

  const { isChecking: isPrimaryChecking, exists: primaryContactExists } = useContactExistsCheck({
    value: primaryContactValue,
    type: resolveContactCheckType(contactType === 'unknown' ? 'email' : contactType),
    excludeUserId: userId,
    skipHydratedValue: true,
  });

  const { isChecking: isSecondaryChecking, exists: contactExists } = useContactExistsCheck({
    value: secondaryContactValue,
    type: resolveContactCheckType(secondaryContactType),
    excludeUserId: userId,
    skipHydratedValue: true,
  });

  const contactsAreIdentical = areContactsIdentical(primaryContactValue, secondaryContactValue);

  /** Hydrate form from full-state once it arrives. */
  React.useEffect(() => {
    if (!fullState?.record) return;
    const next = hydrateParentFormState(fullState.record, defaultCountry);
    setFormData(next.formData);
    const known = resolveKnownContactType(next.formData);
    if (known) setContactType(known);
    validation.validateInitialFormData(next.formData);
  }, [fullState, defaultCountry]);

  const { handleChange, handleBlur, handleContactChange } = useParentFormHandlers({
    formData,
    setFormData,
    contactType,
    setContactType,
    defaultCountry,
    validation,
  });

  /** Swap primary and secondary contact fields. */
  function handleSwapContacts(): void {
    const swapped = applyContactSwapUnified(formData, defaultCountry);
    setContactType(swapped.contactType as ParentContactType);
    setFormData(swapped);
  }

  const updateMutation = useUpdateParentRemoteProfile();
  const contactKind = toPrimaryContactMode(contactType);
  const isPrimaryDirty = isPrimaryContactDirty({
    mode: contactKind,
    currentValue: primaryContactValue,
    storedEmail: record?.user?.email,
    storedPhone: record?.user?.phone,
    serverKind: record?.user?.primaryContactKind,
    defaultCountry,
  });

  /** Close the edit surface after a credential modal or a clean PATCH. */
  function handleContactChangeFinished(): void {
    onSuccess?.();
  }

  const contactChange = useContactChangeOnSubmit({
    isDirty: isPrimaryDirty,
    kind: contactKind,
    ownProfile: false,
    closeForm: handleContactChangeFinished,
  });

  /** Store the file for submit upload and write a pending link so required docs validate. */
  const handleFileChange = (key: string, file: File | null) => {
    setPendingFiles((prev) => ({ ...prev, [key]: file }));
    const patch = buildPendingDocumentFilePatch(key, file);
    if (!patch) return;
    Object.entries(patch).forEach(([field, value]) => {
      handleChange(field, value);
    });
  };

  /** Upload pending files, then PATCH personal-account + contract. */
  const handleSubmit = async () => {
    if (
      isPrimaryContactSubmitBlocked(primaryContactExists, isPrimaryChecking, contactsAreIdentical)
    )
      return;
    if (
      !validation.validateForm(
        parentValidationPayload(formData, contactType, contactExists, primaryContactExists),
      )
    )
      return;

    if (hasPendingUploadFiles(pendingFiles)) {
      setIsUploading(true);
    }
    let uploadedUrls: Record<string, string> = {};
    try {
      uploadedUrls = await uploadPendingFiles(pendingFiles, defaultParentUpload);
    } catch (err) {
      toast.error(`Erreur lors de l'upload : ${getErrorMessage(err)}`);
      setIsUploading(false);
      return;
    }

    const enrichedData = mergePendingUploads(formData, uploadedUrls);

    contactChange.requestSubmit(() => {
      updateMutation.mutate(
        {
          userId,
          dto: buildUpdateParentDto(enrichedData, fullState, defaultCountry),
          companyId,
        },
        {
          onSuccess: (data) => {
            if (contactChange.isIntercepting) {
              contactChange.onMutationSettled({ ok: true, link: readPhoneSetupLink(data) });
              return;
            }
            toast.success('Dossier mis à jour avec succès');
            handleContactChangeFinished();
          },
          onError: (err: unknown) => {
            toast.error(getErrorMessage(err));
            contactChange.onMutationSettled({ ok: false });
          },
        },
      );
    });
    setIsUploading(false);
  };

  const isSubmitValid = isPrimaryContactFormValid({
    primaryExists: primaryContactExists,
    isPrimaryChecking,
    isSecondaryChecking,
    identical: contactsAreIdentical,
    formValid: validation.isFormValid(),
  });

  /** True after GET hydrates formData so fields do not mount empty. */
  const isHydrated = Object.keys(formData).length > 0;
  /** True until GET parent hydrates the form. */
  const isFormLoading = isLoadingFullState || !record || !isHydrated;

  function renderFormSections(): React.ReactNode {
    if (!record || !isHydrated) {
      return null;
    }
    return (
      <>
        <ParentPersonalAccountSection
          formData={formData}
          onChange={handleChange}
          onBlur={handleBlur}
          validation={validation}
          mode="edit"
          defaultCountry={defaultCountry}
          contactType={contactType}
          onContactChange={handleContactChange}
          onSwapContacts={handleSwapContacts}
          userId={userId}
          emailVerified={record.user?.emailVerified}
          contactExists={contactExists}
          primaryContactExists={primaryContactExists}
          contactsAreIdentical={contactsAreIdentical}
          serverEmail={record.user?.email}
          serverPhone={record.user?.phone}
          serverPrimaryKind={record.user?.primaryContactKind}
        />
        <ParentContractSection
          formData={formData}
          onChange={handleChange}
          onFileSelect={handleFileChange}
        />
      </>
    );
  }

  return (
    <>
      <CollapseReplaceForm
        formType="edit"
        title="Modifier le parent"
        subtitle="Modifiez les informations du parent"
        onClose={onClose}
        onSubmit={handleSubmit}
        submitLabel="Enregistrer"
        isSubmitting={isUploading || updateMutation.isPending || contactChange.modal.open}
        isValid={isSubmitValid}
        isLoading={isFormLoading}
      >
        {renderFormSections()}
      </CollapseReplaceForm>
      <ContactChangeModal
        open={contactChange.modal.open}
        kind={contactChange.modal.kind}
        ownProfile={contactChange.modal.ownProfile}
        phase={contactChange.modal.phase}
        phoneSetupLink={contactChange.modal.phoneSetupLink}
        onClose={contactChange.cancel}
        onConfirm={contactChange.confirm}
        onCopyLink={contactChange.onCopyLink}
      />
    </>
  );
}
